import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ActiveAssignment {
  readonly assignmentId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly status: string;
  readonly priority: string;
  readonly expectedStartDate: string | null;
  readonly expectedCompletionDate: string | null;
  readonly acceptanceDueAt: string | null;
  readonly targetSlaMinutes: number | null;
}
interface HistoryEvent {
  readonly historyId?: string;
  readonly eventType: string;
  readonly fromStatus: string | null;
  readonly toStatus: string | null;
  readonly occurredAt: string;
  readonly remarks: string | null;
}
interface VendorResponse {
  readonly responseId?: string;
  readonly response: string;
  readonly respondedAt: string;
  readonly remarks: string | null;
}
interface TimelineData {
  /** The job's number (G18) — the title never shows the uuid in the route. */
  readonly jobNumber: string | null;
  readonly activeAssignment: ActiveAssignment | null;
  readonly history: readonly HistoryEvent[];
  readonly responses: readonly VendorResponse[];
}

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'EMERGENCY'] as const;

export function BoTimelinePage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { jobRequestId } = useParams<{ jobRequestId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<TimelineData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [expectedStartDate, setExpectedStartDate] = useState('');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [targetSlaMinutes, setTargetSlaMinutes] = useState('');

  async function load() {
    try {
      const r = await apiRequest<TimelineData>(`/v1/vendor-assignments/timeline/${jobRequestId}`, { token });
      setData(r);
      if (r.activeAssignment) {
        setExpectedStartDate(r.activeAssignment.expectedStartDate ?? '');
        setExpectedCompletionDate(r.activeAssignment.expectedCompletionDate ?? '');
        setPriority(r.activeAssignment.priority ?? 'MEDIUM');
        setTargetSlaMinutes(r.activeAssignment.targetSlaMinutes != null ? String(r.activeAssignment.targetSlaMinutes) : '');
      }
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [jobRequestId, token]);

  async function save() {
    const a = data?.activeAssignment;
    if (!a) { setError('No active assignment to update'); return; }
    if (!expectedStartDate || !expectedCompletionDate) { setError('Start and completion dates required'); return; }
    if (expectedStartDate > expectedCompletionDate) { setError('Start must be ≤ completion'); return; }
    setError(null); setInfo(null);
    try {
      const body: Record<string, unknown> = { expectedStartDate, expectedCompletionDate, priority };
      if (targetSlaMinutes.trim()) body.targetSlaMinutes = Number(targetSlaMinutes);
      await apiRequest(`/v1/vendor-assignments/${a.assignmentId}/timeline`, { method: 'PUT', token, body });
      setInfo('Timeline updated');
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  if (error && !data) return <main aria-label="bo-timeline"><p role="alert">{error}</p></main>;
  if (!data) return <main aria-label="bo-timeline"><p>{t('bo.timeline.loading')}</p></main>;

  const a = data.activeAssignment;
  return (
    <main aria-label="bo-timeline">
      <h1>{t('bo.timeline.title', { job: data.jobNumber ?? '—' })}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {info ? <p role="status">{info}</p> : null}

      <section aria-label="active-assignment">
        <h2>{t('bo.timeline.activeH')}</h2>
        {a ? (
          <dl>
            <div><dt>{t('bo.timeline.vendor')}</dt><dd aria-label="active-vendor">{personLabel(a.vendorName, a.vendorCode) ?? '—'}</dd></div>
            <div><dt>{t('bo.timeline.status')}</dt><dd aria-label="active-status">{a.status}</dd></div>
            <div><dt>{t('bo.timeline.priority')}</dt><dd aria-label="active-priority">{a.priority}</dd></div>
            <div><dt>{t('bo.timeline.acceptanceDue')}</dt><dd aria-label="active-due">{at(a.acceptanceDueAt)}</dd></div>
          </dl>
        ) : <p aria-label="no-active">{t('bo.timeline.noActive')}</p>}
      </section>

      {a ? (
        <section aria-label="timeline-edit">
          <h2>{t('bo.timeline.updateH')}</h2>
          <label htmlFor="ts-start">{t('bo.timeline.expectedStart')}</label>
          <DateInput id="ts-start" aria-label="expected-start" value={expectedStartDate} onValueChange={setExpectedStartDate} />
          <label htmlFor="ts-end">{t('bo.timeline.expectedCompletion')}</label>
          <DateInput id="ts-end" aria-label="expected-completion" value={expectedCompletionDate} onValueChange={setExpectedCompletionDate} />
          <label htmlFor="ts-priority">{t('bo.timeline.priorityLabel')}</label>
          <select id="ts-priority" aria-label="priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <label htmlFor="ts-sla">{t('bo.timeline.targetSla')}</label>
          <input id="ts-sla" aria-label="target-sla" type="number" value={targetSlaMinutes} onChange={(e) => setTargetSlaMinutes(e.target.value)} />
          <button onClick={save} aria-label="save-timeline">{t('bo.timeline.save')}</button>
        </section>
      ) : null}

      <section aria-label="timeline-history">
        <h2>{t('bo.timeline.historyH')}</h2>
        {data.history.length === 0 ? <p aria-label="no-history">{t('bo.timeline.noHistory')}</p> : (
          <ul aria-label="history-list">
            {data.history.map((h, i) => (
              <li key={h.historyId ?? i} aria-label={`history-${i}`}>
                {at(h.occurredAt)} — {h.eventType}{h.fromStatus ? ` (${h.fromStatus} → ${h.toStatus})` : ''}{h.remarks ? ` · ${h.remarks}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="timeline-responses">
        <h2>{t('bo.timeline.responsesH')}</h2>
        {data.responses.length === 0 ? <p aria-label="no-responses">{t('bo.timeline.noResponses')}</p> : (
          <ul aria-label="response-list">
            {data.responses.map((r, i) => (
              <li key={r.responseId ?? i} aria-label={`response-${i}`}>
                {at(r.respondedAt)} — {r.response}{r.remarks ? ` · ${r.remarks}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
